import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';

import { Toaster, toast } from 'react-hot-toast';
import { getSpendAlerts, acknowledgeSpendAlert, apiClient, type BillingTier } from './api/client';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatStoreProvider, useChatStoreContext } from './context/ChatStoreContext';
import { SwarmProvider } from './swarm/context/SwarmContext';
import { OrchestratorProvider } from './context/OrchestratorLayer';

import AuthPage from './pages/AuthPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import WebsiteV2 from './website/WebsiteV2';
import { websiteApplicationRoutes, websiteSignInDestination } from './website/applicationRoutes';
import { MiraalIcon, MiraalWordmark } from './components/Brand';
import { MiraalMonogram } from './components/Chat/MiraalMonogram';

import { useUserProfile } from './hooks/useUserProfile';
import { LanguageProvider, useLanguage } from './context/LanguageContext';

import { MobileNav } from './components/MobileNav';
import { RealSwarmDemo } from './components/Swarm/RealSwarmDemo';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  MessageSquare, Settings, LogOut, HelpCircle, Mail, Plus, X, Pin, PinOff,
  Trash2, Pencil, PanelLeftOpen, Phone, Trash, Sparkles, ChevronDown, ChevronUp,
  ChevronRight, Zap, Clock, LayoutGrid, Code2, Puzzle, Info, Languages,
  Cpu, Network, Lightbulb, Shield, BarChart3, Users, Gem, Calendar,
  Moon, Sun, Image, FileText, User, Bell, Lock, Smartphone, Database, HardDrive,
  Palette, Search, Search as SearchIcon, Globe, RefreshCw, Loader2,
  ArrowLeft, Server, Globe2, ExternalLink as External, Microscope,
  Compass as CompassIcon, Plug, CreditCard, Monitor
} from 'lucide-react';

import { SiFigma } from 'react-icons/si';
import { motion, AnimatePresence } from 'framer-motion';

const ScheduledTasksPage = React.lazy(() => import('./pages/ScheduledTasksPage').then(module => ({ default: module.ScheduledTasksPage })));
const CodePage = React.lazy(() => import('./pages/CodePage').then(module => ({ default: module.CodePage })));
const AgentSwarmPage = React.lazy(() => import('./pages/AgentSwarmPage'));
const MiraalExplorePage = React.lazy(() => import('./pages/MiraalExplorePage'));
const MiraalChatContainer = React.lazy(() => import('./components/Chat/MiraalChatContainer').then(module => ({ default: module.MiraalChatContainer })));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const DocumentsPage = React.lazy(() => import('./pages/DocumentsPage'));
const TeamsPage = React.lazy(() => import('./pages/TeamsPage'));

const APP_VERSION = '3.0.0';

const glowWhite = '0 0 20px rgba(255,255,255,0.15), 0 0 40px rgba(255,255,255,0.08), 0 0 60px rgba(255,255,255,0.04)';
const glowStrong = '0 0 30px rgba(255,255,255,0.25), 0 0 60px rgba(255,255,255,0.12), 0 0 90px rgba(255,255,255,0.06)';
const glowAccent = '0 0 20px rgba(255,255,255,0.2), 0 0 40px rgba(255,255,255,0.1)';
const glowText = '0 0 10px rgba(255,255,255,0.3), 0 0 20px rgba(255,255,255,0.15)';
const glowCard = '0 0 25px rgba(255,255,255,0.08), 0 0 50px rgba(255,255,255,0.04)';
const glowPulse = '0 0 15px rgba(255,255,255,0.2), 0 0 30px rgba(255,255,255,0.1)';
const glowInput = '0 0 20px rgba(255,255,255,0.06), inset 0 0 10px rgba(255,255,255,0.02)';
const glowButton = '0 0 20px rgba(255,255,255,0.15), 0 0 40px rgba(255,255,255,0.08)';
const glowDot = '0 0 8px rgba(255,255,255,0.6), 0 0 16px rgba(255,255,255,0.3)';
const glowActive = '0 0 20px rgba(255,255,255,0.2), 0 0 40px rgba(255,255,255,0.1)';
const glowBlue = '0 0 20px rgba(59,130,246,0.3), 0 0 40px rgba(59,130,246,0.15)';

// ============================================================================
// HELPERS: localStorage wrappers with error handling
// ============================================================================

const ls = {
  get: (key: string, fallback?: any) => {
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
  },
  set: (key: string, value: any) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* noop */ }
  },
  remove: (key: string) => {
    try { localStorage.removeItem(key); } catch { /* noop */ }
  },
};

// ============================================================================
// THEME CONTEXT - provides real dark/light mode switching
// ============================================================================

// User's stored choice. 'system' follows the OS via prefers-color-scheme.
type ThemePreference = 'dark' | 'light' | 'system';
// What actually gets applied to the DOM.
type ThemeMode = 'dark' | 'light';

const prefersDark = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolveTheme = (pref: ThemePreference): ThemeMode =>
  pref === 'system' ? (prefersDark() ? 'dark' : 'light') : pref;

const ThemeContext = React.createContext<{
  theme: ThemeMode;                 // resolved (never 'system')
  themePreference: ThemePreference; // what the user picked
  setTheme: (t: ThemePreference) => void;
  fontSize: 'compact' | 'comfortable';
  setFontSize: (s: 'compact' | 'comfortable') => void;
}>({
  theme: 'dark',
  themePreference: 'dark',
  setTheme: () => {},
  fontSize: 'comfortable',
  setFontSize: () => {},
});

const applyTheme = (mode: ThemeMode) => {
  document.documentElement.setAttribute('data-theme', mode);
  document.documentElement.classList.toggle('light', mode === 'light');
  document.documentElement.classList.toggle('dark', mode === 'dark');
};

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themePreference, setPrefState] = useState<ThemePreference>(() => ls.get('miraal_theme', 'dark'));
  const [fontSize, setFontSizeState] = useState<'compact' | 'comfortable'>(() => ls.get('miraal_fontsize', 'comfortable'));

  const theme = resolveTheme(themePreference);

  const setTheme = useCallback((t: ThemePreference) => {
    setPrefState(t);
    ls.set('miraal_theme', t);
    applyTheme(resolveTheme(t));
  }, []);

  const setFontSize = useCallback((s: 'compact' | 'comfortable') => {
    setFontSizeState(s);
    ls.set('miraal_fontsize', s);
    document.documentElement.setAttribute('data-fontsize', s);
  }, []);

  useEffect(() => {
    applyTheme(theme);
    document.documentElement.setAttribute('data-fontsize', fontSize);
  }, [theme, fontSize]);

  // Live-follow the OS while the preference is 'system'.
  useEffect(() => {
    if (themePreference !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme(prefersDark() ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [themePreference]);

  return (
    <ThemeContext.Provider value={{ theme, themePreference, setTheme, fontSize, setFontSize }}>
      {children}
    </ThemeContext.Provider>
  );
};
const useTheme = () => React.useContext(ThemeContext);

// ============================================================================
// ANIMATED AVATAR
// ============================================================================

const AnimatedAvatar: React.FC<{ size?: number; letter?: string }> = ({ size = 36, letter = 'M' }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <motion.div
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      className="w-full h-full rounded-full"
      style={{
        background: 'linear-gradient(135deg, #3b82f6, #06b6d4, #8b5cf6)',
        boxShadow: '0 0 20px rgba(59,130,246,0.4), 0 0 40px rgba(59,130,246,0.2)',
      }}
    >
      <div className="absolute inset-0 rounded-full" style={{
        background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.3), transparent 70%)'
      }} />
      <div className="absolute inset-0 flex items-center justify-center">
        <span style={{ 
          color: '#fff', fontSize: size * 0.45, fontWeight: 800,
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>{letter}</span>
      </div>
    </motion.div>
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      className="absolute inset-0"
      style={{ margin: -3 }}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-blue-400" style={{ boxShadow: '0 0 6px rgba(96,165,250,0.8)' }} />
    </motion.div>
    <motion.div
      animate={{ rotate: -360 }}
      transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
      className="absolute inset-0"
      style={{ margin: -5 }}
    >
      <div className="absolute bottom-0 right-1/4 w-1 h-1 rounded-full bg-purple-400" style={{ boxShadow: '0 0 6px rgba(167,139,250,0.8)' }} />
    </motion.div>
  </div>
);

const AnimatedIcon: React.FC<{ 
  icon: React.ElementType; 
  isActive?: boolean; 
  size?: number;
}> = ({ icon: Icon, isActive = false, size = 16 }) => (
  <div className="relative inline-flex items-center justify-center">
    <Icon 
      size={size}
      className={`transition-all duration-300 ${
        isActive 
          ? 'text-white scale-110 drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]' 
          : 'text-gray-400'
      } ${isActive ? 'animate-pulse' : ''}`} 
    />
    {isActive && (
      <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-white rounded-full animate-ping" style={{ boxShadow: glowDot }} />
    )}
  </div>
);

const TypingIndicator: React.FC = () => (
  <div className="flex items-center gap-1 p-2">
    <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms', boxShadow: glowDot }} />
    <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms', boxShadow: glowDot }} />
    <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms', boxShadow: glowDot }} />
  </div>
);


// ============================================================================
// CHAT HISTORY
// ============================================================================

// Uses the same `SB` token object defined in the Sidebar block (module-level
// const, so it's already initialized by the time this component renders â€”
// no import needed, just don't delete that const from the Sidebar block).

const ChatHistorySection: React.FC<{
  sessions: any[]; activeSessionId: string | null;
  onSelectChat: (id: string) => void;
  onRenameChat: (id: string, title: string) => void;
  onDeleteChat: (id: string) => void;
  onTogglePin: (id: string) => void;
  collapsed?: boolean;
}> = ({ sessions, activeSessionId, onSelectChat, onRenameChat, onDeleteChat, onTogglePin, collapsed = false }) => {
  const { t } = useLanguage();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  const startEdit = (session: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(session.id);
    setEditValue(session.title);
  };

  const commitEdit = () => {
    if (editingId && editValue.trim()) onRenameChat(editingId, editValue.trim());
    setEditingId(null);
    setEditValue('');
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setShowDeleteConfirm(id);
  };

  const confirmDelete = (id: string) => {
    onDeleteChat(id);
    setShowDeleteConfirm(null);
    toast.success('Chat deleted');
  };

  const filteredSessions = searchTerm.trim()
    ? sessions.filter((s: any) => s.title.toLowerCase().includes(searchTerm.toLowerCase()) || (s.messages || []).some((m: any) => (m.content || '').toLowerCase().includes(searchTerm.toLowerCase())))
    : [...sessions];

  filteredSessions.sort((a: any, b: any) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return (b.updatedAt || 0) - (a.updatedAt || 0);
  });

  // -- COLLAPSED --
  if (collapsed) {
    return (
      <div className="flex-1 overflow-y-auto px-1.5 pb-2 space-y-0.5 min-h-0">
        {filteredSessions.slice(0, 8).map((session: any) => {
          const isActive = session.id === activeSessionId;
          return (
            <button
              key={session.id}
              onClick={() => onSelectChat(session.id)}
              title={session.title}
              className="w-full flex items-center justify-center p-2 rounded-md transition-colors"
              style={{ backgroundColor: isActive ? SB.active : 'transparent' }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = SB.hover; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <MessageSquare size={14} style={{ color: isActive ? SB.text : SB.textDim }} />
            </button>
          );
        })}
      </div>
    );
  }

  // -- EXPANDED --
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-2.5 py-2 shrink-0">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: SB.textFaint }} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('search')}
            className="w-full pl-8 pr-3 py-1.5 rounded-md text-[12px] focus:outline-none transition-colors"
            style={{ backgroundColor: '#18181b', border: `1px solid ${SB.border}`, color: SB.text }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#3f3f46'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = SB.border; }}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5 min-h-0">
        {filteredSessions.length === 0 && !searchTerm && (
          <div className="text-center mt-8">
            <p className="text-[12px]" style={{ color: SB.textFaint }}>{t('noChats')}</p>
          </div>
        )}

        {filteredSessions.map((session: any) => {
          const isActive = session.id === activeSessionId;
          const isEditing = editingId === session.id;
          const isDeleting = showDeleteConfirm === session.id;
          return (
            <div
              key={session.id}
              onClick={() => !isEditing && !isDeleting && onSelectChat(session.id)}
              className="group flex items-center gap-2 px-2.5 py-2 rounded-md cursor-pointer transition-colors relative"
              style={{ backgroundColor: isActive ? SB.active : 'transparent' }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = SB.hover; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      ref={editInputRef}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                        if (e.key === 'Escape') { setEditingId(null); setEditValue(''); }
                      }}
                      onBlur={commitEdit}
                      className="flex-1 rounded px-2 py-1 text-[12px] focus:outline-none"
                      style={{ backgroundColor: '#0e0e10', border: '1px solid #3f3f46', color: SB.text }}
                    />
                  </div>
                ) : (
                  <div
                    className="text-[12.5px] truncate leading-tight"
                    style={{ color: isActive ? SB.text : SB.textDim, fontWeight: isActive ? 500 : 400 }}
                  >
                    {session.title}
                  </div>
                )}
              </div>

              {!isEditing && !isDeleting && (
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); onTogglePin(session.id); }}
                    className="p-1 rounded transition-colors"
                    style={{ color: SB.textDim }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = SB.text; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = SB.textDim; }}
                  >
                    {session.pinned ? <PinOff size={12} /> : <Pin size={12} />}
                  </button>
                  <button
                    onClick={(e) => startEdit(session, e)}
                    className="p-1 rounded transition-colors"
                    style={{ color: SB.textDim }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = SB.text; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = SB.textDim; }}
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={(e) => handleDelete(e, session.id)}
                    className="p-1 rounded transition-colors"
                    style={{ color: SB.textDim }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#f87171'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = SB.textDim; }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )}

              <AnimatePresence>
                {isDeleting && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.1 }}
                    className="absolute right-0 top-full z-50 rounded-lg p-3 w-48"
                    style={{ backgroundColor: '#18181b', border: `1px solid ${SB.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <p className="text-[12px] mb-2" style={{ color: SB.text }}>Delete this chat?</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => confirmDelete(session.id)}
                        className="flex-1 rounded-md py-1.5 text-[12px] font-medium text-white transition-colors"
                        style={{ backgroundColor: '#dc2626' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#b91c1c'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#dc2626'; }}
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(null)}
                        className="flex-1 rounded-md py-1.5 text-[12px] transition-colors"
                        style={{ backgroundColor: '#27272a', color: SB.text }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#3f3f46'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#27272a'; }}
                      >
                        Cancel
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// WEBSITE SWARM DEMO
// ============================================================================

const WebsiteSwarmDemo: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  type DemoAgentStatus = 'idle' | 'working' | 'done';
interface DemoAgent { id: string; name: string; icon: React.ElementType; status: DemoAgentStatus; task: string; progress: number; dependsOn: string[]; output: string; }
const [agents, setAgents] = useState<DemoAgent[]>([
    { id: 'research', name: 'Research Agent', icon: SearchIcon, status: 'idle', task: '', progress: 0, dependsOn: [] as string[], output: '' },
    { id: 'design', name: 'Design Agent', icon: SiFigma, status: 'idle', task: '', progress: 0, dependsOn: ['research'] as string[], output: '' },
    { id: 'frontend', name: 'Frontend Agent', icon: Code2, status: 'idle', task: '', progress: 0, dependsOn: ['design'] as string[], output: '' },
    { id: 'backend', name: 'Backend Agent', icon: Server, status: 'idle', task: '', progress: 0, dependsOn: ['research'] as string[], output: '' },
    { id: 'devops', name: 'DevOps Agent', icon: Globe2, status: 'idle', task: '', progress: 0, dependsOn: ['frontend', 'backend'] as string[], output: '' },
  ]);
  const [swarmLog, setSwarmLog] = useState<string[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState<'agents' | 'graph' | 'log'>('agents');

  const runSwarm = () => {
    if (isRunning) return;
    setIsRunning(true);
    setSwarmLog(['Orchestrator: Starting website build project...']);
    setAgents(prev => prev.map(a => ({ ...a, status: 'idle', task: '', progress: 0, output: '' })));
    const tasks = [
      { agentId: 'research', task: 'Analyzing website requirements, target audience, and competitor sites', duration: 2500, output: 'Requirements doc: SaaS landing page, dark theme, pricing section, testimonials' },
      { agentId: 'design', task: 'Creating Figma mockups and design system based on research', duration: 3000, output: 'Figma file: 12 screens, component library, color tokens' },
      { agentId: 'frontend', task: 'Building React components with Next.js and Tailwind', duration: 3500, output: 'Code: 15 components, responsive layout, animations' },
      { agentId: 'backend', task: 'Building REST API with Node.js and database schema', duration: 3000, output: 'API: 8 endpoints, PostgreSQL schema, auth middleware' },
      { agentId: 'devops', task: 'Deploying to AWS with CI/CD pipeline and monitoring', duration: 2000, output: 'Live: https://app.miraal.ai, CloudWatch, auto-scaling' },
    ];
    setTimeout(() => {
      const task = tasks[0];
      setAgents(prev => prev.map(a => a.id === task.agentId ? { ...a, status: 'working' as const, task: task.task, progress: 0 } : a));
      setSwarmLog(prev => [...prev, `${task.agentId}: "${task.task}"`]);
      const interval = setInterval(() => {
        setAgents(prev => prev.map(a => {
          if (a.id === task.agentId && a.status === 'working') {
            const newProgress = a.progress + 10;
            if (newProgress >= 100) { clearInterval(interval); return { ...a, progress: 100, status: 'done' as const, output: task.output }; }
            return { ...a, progress: newProgress };
          }
          return a;
        }));
      }, task.duration / 10);
      setTimeout(() => {
        setAgents(prev => prev.map(a => a.id === task.agentId ? { ...a, status: 'done' as const, progress: 100, output: task.output } : a));
        setSwarmLog(prev => [...prev, `${task.agentId}: Requirements analysis complete`]);
        setTimeout(() => {
          const designTask = tasks[1];
          setAgents(prev => prev.map(a => a.id === designTask.agentId ? { ...a, status: 'working' as const, task: designTask.task, progress: 0 } : a));
          setSwarmLog(prev => [...prev, `${designTask.agentId}: "${designTask.task}" (waiting for research -> done)`]);
          const dInterval = setInterval(() => {
            setAgents(prev => prev.map(a => {
              if (a.id === designTask.agentId && a.status === 'working') {
                const newProgress = a.progress + 10;
                if (newProgress >= 100) { clearInterval(dInterval); return { ...a, progress: 100, status: 'done' as const, output: designTask.output }; }
                return { ...a, progress: newProgress };
              }
              return a;
            }));
          }, designTask.duration / 10);
          setTimeout(() => {
            setAgents(prev => prev.map(a => a.id === designTask.agentId ? { ...a, status: 'done' as const, progress: 100, output: designTask.output } : a));
            setSwarmLog(prev => [...prev, `${designTask.agentId}: Design system ready`]);
            [tasks[2], tasks[3]].forEach((task, idx) => {
              setTimeout(() => {
                setAgents(prev => prev.map(a => a.id === task.agentId ? { ...a, status: 'working' as const, task: task.task, progress: 0 } : a));
                setSwarmLog(prev => [...prev, `${task.agentId}: "${task.task}" (parallel execution)`]);
                const interval = setInterval(() => {
                  setAgents(prev => prev.map(a => {
                    if (a.id === task.agentId && a.status === 'working') {
                      const newProgress = a.progress + 10;
                      if (newProgress >= 100) { clearInterval(interval); return { ...a, progress: 100, status: 'done' as const, output: task.output }; }
                      return { ...a, progress: newProgress };
                    }
                    return a;
                  }));
                }, task.duration / 10);
                setTimeout(() => {
                  setAgents(prev => prev.map(a => a.id === task.agentId ? { ...a, status: 'done' as const, progress: 100, output: task.output } : a));
                  setSwarmLog(prev => [...prev, `${task.agentId}: Implementation complete`]);
                  if (idx === 1) {
                    setTimeout(() => {
                      const devopsTask = tasks[4];
                      setAgents(prev => prev.map(a => a.id === devopsTask.agentId ? { ...a, status: 'working' as const, task: devopsTask.task, progress: 0 } : a));
                      setSwarmLog(prev => [...prev, `${devopsTask.agentId}: "${devopsTask.task}" (waiting for frontend + backend -> done)`]);
                      const dInterval = setInterval(() => {
                        setAgents(prev => prev.map(a => {
                          if (a.id === devopsTask.agentId && a.status === 'working') {
                            const newProgress = a.progress + 10;
                            if (newProgress >= 100) { clearInterval(dInterval); return { ...a, progress: 100, status: 'done' as const, output: devopsTask.output }; }
                            return { ...a, progress: newProgress };
                          }
                          return a;
                        }));
                      }, devopsTask.duration / 10);
                      setTimeout(() => {
                        setAgents(prev => prev.map(a => a.id === devopsTask.agentId ? { ...a, status: 'done' as const, progress: 100, output: devopsTask.output } : a));
                        setSwarmLog(prev => [...prev, `${devopsTask.agentId}: Website deployed!`, 'All tasks completed. Swarm disengaged.']);
                        setIsRunning(false);
                      }, devopsTask.duration);
                    }, 500);
                  }
                }, task.duration);
              }, idx * 300);
            });
          }, designTask.duration);
        }, 500);
      }, task.duration);
    }, 500);
  };

  const resetSwarm = () => {
    setAgents(prev => prev.map(a => ({ ...a, status: 'idle', task: '', progress: 0, output: '' })));
    setSwarmLog([]);
    setIsRunning(false);
  };

  if (!isOpen) return null;
  const doneCount = agents.filter(a => a.status === 'done').length;

  return (
    <div className="fixed inset-y-0 right-0 w-[420px] bg-[#121214] border-l border-[#2a2a2e] z-[90] flex flex-col" style={{ boxShadow: glowStrong }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2e] shrink-0">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${isRunning ? 'bg-white animate-pulse' : 'bg-[#333]'}`} style={isRunning ? { boxShadow: glowDot } : {}} />
          <span className="text-[11px] font-medium text-white" style={{ textShadow: glowText }}>{t('websiteExample')}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetSwarm} className="p-1.5 rounded-lg hover:bg-white/10 text-[#999] hover:text-white"><RefreshCw size={13} /></button>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-[#999] hover:text-white"><X size={13} /></button>
        </div>
      </div>
      <div className="flex border-b border-[#2a2a2e] shrink-0">
        {[{ id: 'agents', label: 'Agents' }, { id: 'graph', label: 'Graph' }, { id: 'log', label: 'Log' }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`flex-1 py-2 text-[11px] transition-colors ${activeTab === tab.id ? 'text-white border-b-2 border-white' : 'text-[#999] hover:text-white'}`} style={activeTab === tab.id ? { textShadow: glowText } : {}}>{tab.label}</button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-4 min-h-0">
        {activeTab === 'agents' && (
          <div className="space-y-3">
            {agents.map((agent) => (
              <div key={agent.id} className={`bg-[#1a1a1e] border rounded-lg p-3 transition-all ${agent.status === 'working' ? 'border-white/40' : agent.status === 'done' ? 'border-white/20' : 'border-[#2a2a2e]'}`} style={agent.status === 'working' ? { boxShadow: glowAccent } : agent.status === 'done' ? { boxShadow: glowCard } : {}}>
                <div className="flex items-center gap-2 mb-2">
                  <AnimatedIcon icon={agent.icon} isActive={agent.status === 'working'} size={14} />
                  <span className="text-[11px] text-white" style={{ textShadow: agent.status === 'working' ? glowText : 'none' }}>{agent.name}</span>
                  <span className={`ml-auto text-[9px] px-2 py-0.5 rounded-full ${agent.status === 'idle' ? 'bg-[#222] text-[#999]' : agent.status === 'working' ? 'bg-white/10 text-white animate-pulse' : 'bg-white/10 text-white'}`} style={agent.status === 'working' ? { boxShadow: glowAccent } : {}}>{agent.status === 'idle' ? 'Idle' : agent.status === 'working' ? 'Working' : 'Done'}</span>
                </div>
                {agent.task && <div className="text-[11px] text-[#999] mb-2">{agent.task}</div>}
                {agent.status === 'working' && (
                  <div className="w-full h-1 bg-[#222] rounded-full overflow-hidden mb-2" style={{ boxShadow: glowInput }}>
                    <div className="h-full bg-white transition-all duration-300" style={{ width: `${agent.progress}%`, boxShadow: glowWhite }} />
                  </div>
                )}
                {agent.output && (
                  <div className="text-[11px] text-[#777] bg-[#121214] rounded px-2 py-1.5 border border-[#2a2a2e]" style={{ boxShadow: glowInput }}>
                    <span className="text-[#999]">Output:</span> {agent.output}
                  </div>
                )}
                {agent.dependsOn.length > 0 && agent.status === 'idle' && !isRunning && (
                  <div className="text-[9px] text-[#777] mt-1">{t('dependsOn')}: {agent.dependsOn.join(', ')}</div>
                )}
              </div>
            ))}
          </div>
        )}
        {activeTab === 'graph' && (
          <div className="space-y-4">
            <div className="text-[11px] text-[#777] uppercase tracking-wider" style={{ textShadow: glowText }}>{t('executionGraph')}</div>
            <div className="space-y-3">
              {agents.map((agent) => (
                <div key={agent.id} className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${agent.status === 'done' ? 'bg-white/10' : agent.status === 'working' ? 'bg-white/10 animate-pulse' : 'bg-[#1a1a1a]'}`} style={agent.status === 'working' ? { boxShadow: glowAccent } : agent.status === 'done' ? { boxShadow: glowCard } : {}}>
                    <AnimatedIcon icon={agent.icon} isActive={agent.status === 'working'} size={14} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] text-white">{agent.name}</div>
                    <div className="text-[9px] text-[#777]">{agent.dependsOn.length > 0 ? `${t('dependsOn')}: ${agent.dependsOn.join(' + ')}` : t('parallel')}</div>
                  </div>
                  <div className={`w-2 h-2 rounded-full ${agent.status === 'done' ? 'bg-white' : agent.status === 'working' ? 'bg-white animate-pulse' : 'bg-[#333]'}`} style={agent.status === 'working' ? { boxShadow: glowDot } : {}} />
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-[#1a1a1e] border border-[#2a2a2e] rounded-lg" style={{ boxShadow: glowCard }}>
              <div className="text-[11px] text-[#999] mb-2">{t('orchestrator')} Flow:</div>
              <div className="text-[11px] text-[#ddd] space-y-1">
                <div>1. Research -&gt; starts first</div>
                <div>2. Design -&gt; waits for Research</div>
                <div>3. Frontend + Backend -&gt; parallel</div>
                <div>4. DevOps -&gt; waits for Frontend + Backend</div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'log' && (
          <div className="bg-[#1a1a1e] border border-[#2a2a2e] rounded-lg p-3" style={{ boxShadow: glowCard }}>
            <div className="text-[11px] text-[#777] uppercase tracking-wider mb-2">{t('taskQueue')}</div>
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {swarmLog.length === 0 && <div className="text-[11px] text-[#bbb]">No activity yet. Click "Run Swarm" to start.</div>}
              {swarmLog.map((log, i) => (
                <div key={i} className="text-[11px] text-[#ddd] font-mono">{log}</div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="p-4 border-t border-[#2a2a2e] shrink-0">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] text-[#999]">{doneCount}/{agents.length} done</span>
          <span className="text-[11px] text-[#777]">{isRunning ? t('swarmActive') : 'Ready'}</span>
        </div>
        <button onClick={runSwarm} disabled={isRunning} className={`w-full py-2.5 rounded-lg text-[11px] font-medium transition-all ${isRunning ? 'bg-[#222] text-[#999] cursor-not-allowed' : 'bg-white text-black hover:bg-[#ddd]'}`} style={!isRunning ? { boxShadow: glowButton } : {}}>
          {isRunning ? (<span className="flex items-center justify-center gap-2"><Loader2 size={13} className="animate-spin" /> Running...</span>) : 'Run Website Build Swarm'}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// PLUGINS PAGE
// ============================================================================

const PluginsPage: React.FC = () => {
  const { t } = useLanguage(); 
  const navigate = useNavigate();
  const [installed, setInstalled] = useState<string[]>(() => ls.get('miraal_plugins', ['web-search', 'file-reader']));

  const plugins = [
    { id: 'web-search', name: 'Web Search', desc: 'Search the internet in real-time', icon: Globe },
    { id: 'file-reader', name: 'File Reader', desc: 'Read PDF, DOC, CSV, and more', icon: FileText },
    { id: 'code-interpreter', name: 'Code Interpreter', desc: 'Run Python, JavaScript, and SQL', icon: Code2 },
    { id: 'image-gen', name: 'Image Generator', desc: 'Generate images from text prompts', icon: Image },
    { id: 'data-viz', name: 'Data Visualizer', desc: 'Create charts and dashboards', icon: BarChart3 },
    { id: 'email-sender', name: 'Email Sender', desc: 'Draft and send emails', icon: Mail },
    { id: 'calendar-sync', name: 'Calendar Sync', desc: 'Connect Google/Outlook calendar', icon: Calendar },
    { id: 'slack-connect', name: 'Slack Connect', desc: 'Send messages to Slack channels', icon: Users },
  ];

  const togglePlugin = (id: string) => {
    setInstalled(prev => {
      const next = prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id];
      ls.set('miraal_plugins', next);
      toast.success(prev.includes(id) ? `${id} uninstalled` : `${id} installed`);
      return next;
    });
  };

  return (
    <div className="h-full bg-[color:var(--bg,#151515)] text-[color:var(--text,#F0EFEC)] p-8 overflow-y-auto">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white" style={{ textShadow: glowText }}>{t('plugins')}</h1>
            <p className="text-[11px] text-[#999] mt-1">{t('extendMiraal')}</p>
          </div>
          <button onClick={() => navigate('/')} className="p-2 rounded-lg hover:bg-white/10 text-[#999]"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {plugins.map((plugin) => { 
            const isInstalled = installed.includes(plugin.id); 
            const Icon = plugin.icon;
            return (
              <div key={plugin.id} className="bg-[#1a1a1e] border border-[#2a2a2e] rounded-xl p-4 hover:border-white/30 transition-all" style={{ boxShadow: glowCard }}>
                <div className="flex items-start justify-between mb-2">
                  <div className="w-10 h-10 rounded-lg bg-[#1a1a1a] flex items-center justify-center" style={{ boxShadow: glowInput }}>
                    <AnimatedIcon icon={Icon} isActive={isInstalled} size={18} />
                  </div>
                  <button onClick={() => togglePlugin(plugin.id)} className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${isInstalled ? 'bg-white/10 text-white border border-white/20' : 'bg-white text-black hover:bg-[#ddd]'}`} style={!isInstalled ? { boxShadow: glowButton } : {}}>
                    {isInstalled ? t('installed') : t('install')}
                  </button>
                </div>
                <div className="text-[11px] font-medium text-white mb-1">{plugin.name}</div>
                <div className="text-[11px] text-[#999]">{plugin.desc}</div>
              </div>
            ); 
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SCHEDULED TASKS PAGE
// ============================================================================

// ============================================================================
// PRICING OVERLAY
// ============================================================================

// Real 3-tier pricing from GET /api/v1/billing/tiers (river display names Free /
// Ganga / Yamuna). Subscribe -> POST /api/v1/billing/plan (self-serve, no
// payment gateway yet). Enterprise is a static contact card - there is no
// backend tier for it (custom pricing).
const TIER_ICONS: Record<string, any> = { starter: Zap, pro: Cpu, business: Network };
const TIER_ORDER = ['starter', 'pro', 'business'];

// Lazy-load Razorpay's checkout.js only when someone actually opens a paid
// checkout - no reason to ship a payments SDK on every page load. Cached so
// re-opening the pricing modal doesn't re-inject the script tag.
declare global { interface Window { Razorpay?: any } }
let _razorpayLoad: Promise<void> | null = null;
const loadRazorpayCheckout = (): Promise<void> => {
  if (window.Razorpay) return Promise.resolve();
  if (_razorpayLoad) return _razorpayLoad;
  _razorpayLoad = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => { _razorpayLoad = null; reject(new Error('Could not load the payment SDK')); };
    document.body.appendChild(script);
  });
  return _razorpayLoad;
};

const PricingOverlay: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const [tiers, setTiers] = useState<Record<string, BillingTier> | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('starter');
  const [loading, setLoading] = useState(true);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [tRes, meRes] = await Promise.all([
        apiClient.billing.getTiers(),
        apiClient.billing.getMe().catch(() => null),
      ]);
      setTiers(tRes.tiers);
      if (meRes) setCurrentPlan(meRes.plan);
    } catch (e: any) {
      setErr(e?.message || 'Could not load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) refresh();
  }, [isOpen, refresh]);

  if (!isOpen) return null;

  // Paid tiers (Pro/Business, once their Razorpay plan_id is configured)
  // go through real recurring billing - Razorpay Checkout opened with a
  // subscription_id (Subscriptions API), not an order_id (that's the
  // one-time Orders API, wrong tool for recurring). Free tiers keep the
  // existing instant self-serve switch.
  const subscribeWithRazorpay = async (key: string) => {
    setBusyPlan(key);
    try {
      const [sub] = await Promise.all([apiClient.billing.subscribe(key), loadRazorpayCheckout()]);
      const rzp = new window.Razorpay({
        key: sub.keyId,
        subscription_id: sub.subscriptionId,
        name: 'Miraal',
        description: `${sub.planRiver} (${sub.planLabel}) - ₹${sub.priceInrMonth}/month`,
        prefill: sub.prefill,
        theme: { color: '#2563eb' },
        handler: async (response: any) => {
          // Fast UI confirmation only - the subscription.charged webhook is
          // what actually grants credits and flips the plan server-side.
          try {
            await apiClient.billing.verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            });
          } catch { /* webhook is still the source of truth either way */ }
          toast.success(`Payment received — activating ${sub.planRiver}. This can take a few seconds.`);
          window.dispatchEvent(new Event('miraal-plan-changed'));
          setBusyPlan(null);
          refresh();
        },
        modal: {
          ondismiss: () => setBusyPlan(null),
        },
      });
      rzp.on('payment.failed', (resp: any) => {
        toast.error(resp?.error?.description || 'Payment failed. No charge was made.');
        setBusyPlan(null);
      });
      rzp.open();
    } catch (e: any) {
      toast.error(e?.message || 'Could not start checkout');
      setBusyPlan(null);
    }
  };

  const subscribe = async (key: string) => {
    if (tiers?.[key]?.requiresPayment) {
      await subscribeWithRazorpay(key);
      return;
    }
    setBusyPlan(key);
    try {
      const res = await apiClient.billing.changePlan(key);
      setCurrentPlan(res.plan);
      toast.success(res.message || `You're now on ${tiers?.[key]?.river || key}${res.credits != null ? ` — ${res.credits} credits` : ''}`);
      window.dispatchEvent(new Event('miraal-plan-changed'));
      refresh();
    } catch (e: any) {
      toast.error(e?.message || 'Could not change plan');
    } finally {
      setBusyPlan(null);
    }
  };

  const ordered = tiers ? TIER_ORDER.filter((k) => tiers[k]).map((k) => [k, tiers[k]] as const) : [];
  const rows: { label: string; val: (t: BillingTier) => React.ReactNode }[] = [
    { label: 'Agent credits / month', val: (t) => t.monthlyCredits.toLocaleString() },
    { label: 'Concurrent builds', val: (t) => t.maxConcurrentSwarms },
    { label: 'Build depth (sections)', val: (t) => t.buildDepth },
    { label: 'QA revision rounds', val: (t) => t.maxRevisions },
    { label: 'Premium AI agents', val: (t) => (t.premiumAgents.length ? t.premiumAgents.join(', ') : <span className="text-[#777]">—</span>) },
    { label: 'Output bundles', val: (t) => t.outputBundles.map((b) => b.replace('_', ' ')).join(', ') },
    { label: 'Monthly spend cap', val: (t) => `₹${t.monthlySpendCapInr.toLocaleString()}` },
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md" onClick={onClose}>
      <div className="h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="max-w-5xl mx-auto p-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-xl font-bold text-white" style={{ textShadow: glowText }}>Plans</h1>
              <p className="text-[11px] text-[#999] mt-1">Change anytime. New tier credits are granted immediately.</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-[#999]"><X size={18} /></button>
          </div>

          {loading && <div className="text-[12px] text-[#999] py-10 text-center">Loading plans…</div>}
          {err && <div className="text-[12px] text-red-400 py-10 text-center">{err}</div>}

          {!loading && !err && tiers && (
            <>
              <div className="grid gap-4 mb-6 md:grid-cols-4">
                {ordered.map(([key, tier]) => {
                  const Icon = TIER_ICONS[key] || Zap;
                  const isCurrent = currentPlan === key;
                  return (
                    <div
                      key={key}
                      className={`relative bg-[#1a1a1e] border rounded-xl p-5 ${isCurrent ? 'border-white/40' : 'border-[#2a2a2e]'}`}
                      style={{ boxShadow: glowCard }}
                    >
                      {isCurrent && (
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-white text-black text-[9px] font-medium">
                          Current plan
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-3">
                        <Icon size={16} className="text-white" />
                        <div className="text-[13px] font-semibold text-white">{tier.river}</div>
                        {tier.river !== tier.label && <span className="text-[10px] text-[#777]">({tier.label})</span>}
                      </div>
                      <div className="text-[18px] font-bold text-white">
                        {tier.priceInrMonth === 0 ? 'Free' : `₹${tier.priceInrMonth.toLocaleString()}`}
                        {tier.priceInrMonth > 0 && <span className="text-[11px] text-[#999] font-normal"> /mo</span>}
                      </div>
                      <div className="text-[11px] text-[#999] mt-1 mb-4">{tier.monthlyCredits.toLocaleString()} credits / month</div>
                      <button
                        onClick={() => subscribe(key)}
                        disabled={isCurrent || busyPlan === key}
                        className={`w-full py-2 rounded-lg text-[11px] font-medium transition-all ${
                          isCurrent
                            ? 'bg-white/5 text-[#777] cursor-default'
                            : 'bg-white text-black hover:bg-[#ddd]'
                        }`}
                        style={!isCurrent ? { boxShadow: glowButton } : {}}
                      >
                        {isCurrent ? 'Your plan' : busyPlan === key ? 'Switching…' : tier.priceInrMonth === 0 ? 'Switch to Free' : `Subscribe to ${tier.river}`}
                      </button>
                    </div>
                  );
                })}

                {/* Enterprise - not a backend tier, custom pricing */}
                <div className="relative bg-[#1a1a1e] border border-[#2a2a2e] rounded-xl p-5" style={{ boxShadow: glowCard }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Gem size={16} className="text-white" />
                    <div className="text-[13px] font-semibold text-white">Enterprise</div>
                  </div>
                  <div className="text-[18px] font-bold text-white">Custom</div>
                  <div className="text-[11px] text-[#999] mt-1 mb-4">Volume credits, SSO, priority support</div>
                  <a
                    href="mailto:sales@miraal.ai?subject=Miraal%20Enterprise"
                    className="block w-full py-2 rounded-lg text-[11px] font-medium text-center bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-all"
                  >
                    Contact sales
                  </a>
                </div>
              </div>

              <p className="text-[11px] text-[#bbb] mb-4">
                Self-serve plan switching is live; card payment is not wired yet, so switching grants the tier immediately at no charge during this phase.
              </p>

              <div className="bg-[#1a1a1e] border border-[#2a2a2e] rounded-xl overflow-hidden" style={{ boxShadow: glowCard }}>
                <div className="grid gap-3 px-4 py-3 border-b border-[#2a2a2e] bg-[#121214]" style={{ gridTemplateColumns: `1.4fr repeat(${ordered.length}, 1fr)` }}>
                  <div className="text-[11px] text-[#999] font-medium">Feature</div>
                  {ordered.map(([key, tier]) => (
                    <div key={key} className="text-[11px] text-white text-center font-medium">{tier.river}</div>
                  ))}
                </div>
                {rows.map((row) => (
                  <div key={row.label} className="grid gap-3 px-4 py-2.5 border-b border-[#2a2a2e] last:border-0" style={{ gridTemplateColumns: `1.4fr repeat(${ordered.length}, 1fr)` }}>
                    <div className="text-[11px] text-[#ddd]">{row.label}</div>
                    {ordered.map(([key, tier]) => (
                      <div key={key} className="text-[11px] text-white text-center">{row.val(tier)}</div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SETTINGS PAGE - PREMIUM ENTERPRISE UI
// ============================================================================

type SettingsTab =
  | 'general' | 'personalization' | 'appearance'
  | 'privacy' | 'notifications' | 'connectors' | 'subscription' | 'account';

const ToggleSwitch: React.FC<{ enabled: boolean; onChange: () => void; disabled?: boolean }> = ({ enabled, onChange, disabled }) => (
  <button
    onClick={disabled ? undefined : onChange}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-blue-600' : 'bg-gray-600'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

// Small pill for features that are intentionally not built yet. Keeping the
// row visible (not hidden) matches production-app conventions; the badge makes
// it unambiguous that the control is inert.
const SoonBadge: React.FC<{ label?: string }> = ({ label = 'Coming soon' }) => (
  <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/25">
    {label}
  </span>
);

const SettingsPageFixed: React.FC = () => {
  const { theme, themePreference, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { sessions, setSessions, setActiveSessionId } = useChatStoreContext();

  const isDark = theme === 'dark';
  const cardBg = isDark ? 'bg-[#111114]' : 'bg-white';
  const borderColor = isDark ? 'border-[#1f1f23]' : 'border-gray-200';
  const textColor = isDark ? 'text-gray-200' : 'text-gray-800';
  const subTextColor = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputBg = isDark ? 'bg-[#08080a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900';

  const [activeTab, setActiveTab] = useState<SettingsTab>('general');

  // ---- Profile / Personalization (real: user_preferences, migrations 010/011) ----
  const [name, setName] = useState(user?.firstName || '');
  const [nickname, setNickname] = useState('');
  const [workType, setWorkType] = useState('');
  const [instructions, setInstructions] = useState('');
  const [responseStyle, setResponseStyle] = useState<'default' | 'concise' | 'explanatory' | 'formal'>('default');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPersonalization, setSavingPersonalization] = useState(false);

  // Real billing snapshot for the Billing tab.
  const [billing, setBilling] = useState<Awaited<ReturnType<typeof apiClient.billing.getMe>> | null>(null);
  const [billingErr, setBillingErr] = useState<string | null>(null);

  // Load real preferences from the backend on mount.
  useEffect(() => {
    let cancelled = false;
    apiClient.preferences.get()
      .then((res) => {
        if (cancelled) return;
        const p = res.preferences || {};
        if (p.displayName) setName(p.displayName);
        if (typeof p.nickname === 'string') setNickname(p.nickname);
        if (typeof p.workType === 'string') setWorkType(p.workType);
        if (typeof p.customInstructions === 'string') {
          setInstructions(p.customInstructions);
          localStorage.setItem('miraal_instructions', p.customInstructions);
        }
        if (p.responseStyle) setResponseStyle(p.responseStyle);
      })
      .catch(() => {
        const cached = localStorage.getItem('miraal_instructions');
        if (cached && !cancelled) setInstructions(cached);
      });
    return () => { cancelled = true; };
  }, []);

  const loadBilling = useCallback(() => {
    apiClient.billing.getMe()
      .then((b) => { setBilling(b); setBillingErr(null); })
      .catch((e: any) => setBillingErr(e?.message || 'Could not load billing'));
  }, []);

  useEffect(() => {
    loadBilling();
    window.addEventListener('miraal-plan-changed', loadBilling);
    return () => window.removeEventListener('miraal-plan-changed', loadBilling);
  }, [loadBilling]);

  // Keep the local display-name cache that the sidebar / empty-state read.
  const syncLocalProfile = () => {
    try {
      const prev = JSON.parse(localStorage.getItem('miraal_profile') || '{}');
      localStorage.setItem('miraal_profile', JSON.stringify({ ...prev, name: name || prev.name, nickname }));
    } catch { /* noop */ }
    localStorage.setItem('miraal_name', name);
    window.dispatchEvent(new Event('miraal-profile-updated'));
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    syncLocalProfile();
    try {
      await apiClient.preferences.update({
        display_name: name, nickname, work_type: workType,
      });
      toast.success('Profile saved');
    } catch (e: any) {
      toast.error(e?.message || 'Saved locally, but syncing failed');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePersonalization = async () => {
    setSavingPersonalization(true);
    localStorage.setItem('miraal_instructions', instructions);
    window.dispatchEvent(new Event('miraal-profile-updated'));
    try {
      await apiClient.preferences.update({
        custom_instructions: instructions, response_style: responseStyle,
      });
      toast.success('Personalization saved');
    } catch (e: any) {
      toast.error(e?.message || 'Saved locally, but syncing failed');
    } finally {
      setSavingPersonalization(false);
    }
  };

  const handleExportData = () => {
    const data = { profile: { name, nickname, workType }, chatSessions: sessions, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `miraal-export.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Data exported');
  };

  const handleClearLocalData = () => {
    localStorage.clear();
    setSessions([]);
    setActiveSessionId(null);
    logout();
    navigate('/auth');
  };

  const RESPONSE_STYLES: { id: typeof responseStyle; label: string; hint: string }[] = [
    { id: 'default', label: 'Normal', hint: 'Miraal decides the length and depth.' },
    { id: 'concise', label: 'Concise', hint: 'Shorter, more direct answers.' },
    { id: 'explanatory', label: 'Explanatory', hint: 'More reasoning and background.' },
    { id: 'formal', label: 'Formal', hint: 'A more professional tone.' },
  ];

  const tabs = [
    { id: 'general', label: 'General', icon: User },
    { id: 'personalization', label: 'Personalization', icon: Sparkles },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'privacy', label: 'Privacy & Data', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'connectors', label: 'Connectors', icon: Plug },
    { id: 'subscription', label: 'Billing', icon: CreditCard },
    { id: 'account', label: 'Account', icon: Lock },
  ] as const;

  return (
    <div className={`h-full flex ${isDark ? 'bg-[#08080a]' : 'bg-[#f9f9f9]'} ${textColor}`}>
      {/* Settings Sidebar */}
      <div className={`w-64 border-r ${borderColor} p-4 shrink-0 ${isDark ? 'bg-[#0a0a0a]' : 'bg-[#f0f0f0]'}`}>
        <div className="flex items-center gap-2 mb-8 px-2">
          <button onClick={() => navigate('/')} className={`p-1.5 rounded-md ${isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}>
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-[16px] font-semibold">Settings</h1>
        </div>
        <div className="space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-[14px] transition-colors ${
                  activeTab === tab.id
                    ? isDark ? 'bg-[#1f1f23] text-white' : 'bg-gray-200 text-gray-900'
                    : `${subTextColor} ${isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'}`
                }`}
              >
                <Icon size={16} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Settings Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-8 md:p-12">

          {/* ========== GENERAL / PROFILE ========== */}
          {activeTab === 'general' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-[22px] font-semibold mb-1">General</h2>
                <p className={`text-[14px] ${subTextColor}`}>Your profile. These details are shared with Miraal in every chat.</p>
              </div>

              <div className={`p-6 rounded-xl border ${borderColor} ${cardBg} space-y-6`}>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-[24px] font-bold text-white">
                    {(name || user?.firstName || 'M')[0]?.toUpperCase()}
                  </div>
                  <div className="flex items-center gap-2">
                    <button disabled className={`px-4 py-2 rounded-md text-[13px] font-medium opacity-50 cursor-not-allowed ${isDark ? 'bg-[#333]' : 'bg-gray-200'}`}>
                      Change avatar
                    </button>
                    <SoonBadge />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-[13px] font-medium mb-2 ${subTextColor}`}>Full name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} className={`w-full px-3 py-2.5 rounded-md border text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/40 ${inputBg}`} />
                  </div>
                  <div>
                    <label className={`block text-[13px] font-medium mb-2 ${subTextColor}`}>What should Miraal call you?</label>
                    <input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="Nickname" className={`w-full px-3 py-2.5 rounded-md border text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/40 ${inputBg}`} />
                  </div>
                </div>

                <div>
                  <label className={`block text-[13px] font-medium mb-2 ${subTextColor}`}>What best describes your work?</label>
                  <input value={workType} onChange={(e) => setWorkType(e.target.value)} placeholder="e.g. Software engineer, Student, Founder, Designer" className={`w-full px-3 py-2.5 rounded-md border text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/40 ${inputBg}`} />
                </div>

                <div className="flex justify-end">
                  <button onClick={handleSaveProfile} disabled={savingProfile} className="px-5 py-2.5 rounded-md bg-blue-600 text-white text-[14px] font-medium hover:bg-blue-700 disabled:opacity-60">{savingProfile ? 'Saving…' : 'Save changes'}</button>
                </div>
              </div>
            </div>
          )}

          {/* ========== PERSONALIZATION ========== */}
          {activeTab === 'personalization' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-[22px] font-semibold mb-1">Personalization</h2>
                <p className={`text-[14px] ${subTextColor}`}>Shape how Miraal responds to you across all chats.</p>
              </div>

              <div className={`p-6 rounded-xl border ${borderColor} ${cardBg} space-y-6`}>
                <div>
                  <label className={`block text-[13px] font-medium mb-2 ${subTextColor}`}>Instructions for Miraal</label>
                  <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={4} placeholder="e.g. When learning new concepts, I find analogies particularly helpful. Skip the recap of my question." className={`w-full px-3 py-2.5 rounded-md border text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/40 resize-none ${inputBg}`} />
                  <p className={`text-[12px] mt-2 ${subTextColor}`}>Miraal keeps these in mind in every chat.</p>
                </div>

                <div className="border-t pt-6 border-gray-700">
                  <p className="text-[14px] font-medium mb-1">Response style</p>
                  <p className={`text-[13px] ${subTextColor} mb-3`}>A default length and tone for replies. Your instructions above still take priority.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {RESPONSE_STYLES.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setResponseStyle(s.id)}
                        title={s.hint}
                        className={`px-3 py-2.5 rounded-md text-[13px] font-medium border transition-colors ${
                          responseStyle === s.id
                            ? 'bg-blue-600 text-white border-blue-600'
                            : `${inputBg} hover:border-blue-500/50`
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <p className={`text-[12px] mt-2 ${subTextColor}`}>{RESPONSE_STYLES.find((s) => s.id === responseStyle)?.hint}</p>
                </div>

                <div className="flex justify-end">
                  <button onClick={handleSavePersonalization} disabled={savingPersonalization} className="px-5 py-2.5 rounded-md bg-blue-600 text-white text-[14px] font-medium hover:bg-blue-700 disabled:opacity-60">{savingPersonalization ? 'Saving…' : 'Save changes'}</button>
                </div>
              </div>
            </div>
          )}

          {/* ========== APPEARANCE ========== */}
          {activeTab === 'appearance' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-[22px] font-semibold mb-1">Appearance</h2>
                <p className={`text-[14px] ${subTextColor}`}>How Miraal looks on this device. Saved in this browser.</p>
              </div>

              <div className={`p-6 rounded-xl border ${borderColor} ${cardBg} space-y-4`}>
                <p className="text-[14px] font-medium">Theme</p>
                <div className="grid grid-cols-3 gap-3">
                  {([
                    { id: 'light', label: 'Light', icon: Sun },
                    { id: 'dark', label: 'Dark', icon: Moon },
                    { id: 'system', label: 'System', icon: Monitor },
                  ] as const).map((opt) => {
                    const OptIcon = opt.icon;
                    const active = themePreference === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setTheme(opt.id)}
                        className={`flex flex-col items-center gap-2 px-3 py-4 rounded-lg border text-[13px] font-medium transition-colors ${
                          active ? 'bg-blue-600 text-white border-blue-600' : `${inputBg} hover:border-blue-500/50`
                        }`}
                      >
                        <OptIcon size={18} />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className={`text-[12px] ${subTextColor}`}>
                  {themePreference === 'system'
                    ? 'Following your operating system setting.'
                    : `Always ${themePreference}.`}
                </p>
              </div>
            </div>
          )}

          {/* ========== NOTIFICATIONS ========== */}
          {activeTab === 'notifications' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-[22px] font-semibold mb-1">Notifications</h2>
                <p className={`text-[14px] ${subTextColor}`}>Alerts for activity in Miraal.</p>
              </div>

              <div className={`p-6 rounded-xl border ${borderColor} ${cardBg} space-y-5`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-[14px] font-medium">Response completions</p>
                    <p className={`text-[13px] ${subTextColor}`}>Notify me when Miraal finishes a long response or a build.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <SoonBadge />
                    <ToggleSwitch enabled={false} onChange={() => {}} disabled />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4 border-t pt-5 border-gray-700">
                  <div>
                    <p className="text-[14px] font-medium">Email updates</p>
                    <p className={`text-[13px] ${subTextColor}`}>Product news and account notices by email.</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <SoonBadge />
                    <ToggleSwitch enabled={false} onChange={() => {}} disabled />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ========== CONNECTORS ========== */}
          {activeTab === 'connectors' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-[22px] font-semibold mb-1">Connectors</h2>
                <p className={`text-[14px] ${subTextColor}`}>Link external apps so Miraal can use them for context. OAuth is not built yet — none of these are active.</p>
              </div>

              <div className={`p-6 rounded-xl border ${borderColor} ${cardBg}`}>
                <div className="space-y-4">
                  {[
                    { id: 'gmail', name: 'Gmail', type: 'Email' },
                    { id: 'gdrive', name: 'Google Drive', type: 'Files' },
                    { id: 'slack', name: 'Slack', type: 'Messaging' },
                    { id: 'github', name: 'GitHub', type: 'Code' },
                  ].map((conn) => (
                    <div key={conn.id} className="flex items-center justify-between border-b pb-4 last:border-0 last:pb-0 border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${isDark ? 'bg-[#1f1f23]' : 'bg-gray-100'}`}>
                          <Plug size={18} />
                        </div>
                        <div>
                          <p className="text-[14px] font-medium">{conn.name}</p>
                          <p className={`text-[12px] ${subTextColor}`}>{conn.type}</p>
                        </div>
                      </div>
                      <SoonBadge />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ========== SUBSCRIPTION ========== */}
          {activeTab === 'subscription' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-[22px] font-semibold mb-1">Subscription</h2>
                <p className={`text-[14px] ${subTextColor}`}>Manage your billing and plan.</p>
              </div>

              <div className={`p-6 rounded-xl border ${borderColor} ${cardBg}`}>
                {billingErr && <p className="text-[13px] text-red-400 mb-4">{billingErr}</p>}
                {!billing && !billingErr && <p className={`text-[13px] ${subTextColor}`}>Loading your plan…</p>}
                {billing && (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <p className={`text-[13px] ${subTextColor}`}>Current Plan</p>
                        <h3 className="text-[20px] font-bold text-blue-400">
                          {billing.river}
                          {billing.river !== billing.label && <span className={`text-[14px] font-normal ${subTextColor}`}> ({billing.label})</span>}
                        </h3>
                        <p className={`text-[13px] ${subTextColor} mt-1`}>
                          {billing.priceInrMonth === 0 ? 'Free plan' : `₹${billing.priceInrMonth.toLocaleString()} / month`}
                        </p>
                      </div>
                      <button
                        onClick={() => window.dispatchEvent(new Event('miraal-open-pricing'))}
                        className="px-5 py-2.5 rounded-md bg-blue-600 text-white text-[14px] font-medium hover:bg-blue-700"
                      >
                        Change Plan
                      </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-gray-700">
                      <div>
                        <p className={`text-[13px] ${subTextColor}`}>Credits balance</p>
                        <p className="text-[18px] font-semibold">{billing.credits.toLocaleString()}</p>
                        <p className={`text-[12px] ${subTextColor}`}>of {billing.monthlyCredits.toLocaleString()} / month</p>
                      </div>
                      <div>
                        <p className={`text-[13px] ${subTextColor}`}>Spend this month</p>
                        <p className="text-[18px] font-semibold">₹{Math.round(billing.monthlySpendInr).toLocaleString()}</p>
                        <p className={`text-[12px] ${subTextColor}`}>of ₹{billing.monthlyCapInr.toLocaleString()} cap ({billing.pctUsed}%)</p>
                      </div>
                      <div>
                        <p className={`text-[13px] ${subTextColor}`}>Active builds</p>
                        <p className="text-[18px] font-semibold">{billing.activeSwarms} / {billing.maxConcurrentSwarms}</p>
                      </div>
                      <div>
                        <p className={`text-[13px] ${subTextColor}`}>Build depth</p>
                        <p className="text-[18px] font-semibold">{billing.buildDepth} sections</p>
                        <p className={`text-[12px] ${subTextColor}`}>{billing.maxRevisions} QA rounds</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ========== PRIVACY & DATA ========== */}
          {activeTab === 'privacy' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-[22px] font-semibold mb-1">Privacy &amp; Data</h2>
                <p className={`text-[14px] ${subTextColor}`}>Export or clear the data Miraal holds for you.</p>
              </div>

              <div className={`p-6 rounded-xl border ${borderColor} ${cardBg}`}>
                <h3 className="text-[16px] font-medium mb-1">Your data</h3>
                <p className={`text-[13px] ${subTextColor} mb-5`}>
                  Miraal does not use your chats to train models and does not collect location data.
                  Full data-handling detail is in the <button onClick={() => navigate('/privacy')} className="text-blue-400 underline">Privacy Policy</button>.
                </p>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[14px]">Export data</p>
                    <p className={`text-[12px] ${subTextColor}`}>Download your profile and chat history as JSON.</p>
                  </div>
                  <button onClick={handleExportData} className={`shrink-0 ml-4 px-4 py-2 rounded-md text-[13px] font-medium ${isDark ? 'bg-[#333] hover:bg-[#444]' : 'bg-gray-200 hover:bg-gray-300'}`}>Export</button>
                </div>
                <div className="flex items-center justify-between border-t pt-4 border-gray-700">
                  <div>
                    <p className="text-[14px]">Delete all chat history</p>
                    <p className={`text-[12px] ${subTextColor}`}>Removes every conversation from this account. Cannot be undone.</p>
                  </div>
                  <button onClick={() => { setSessions([]); setActiveSessionId(null); toast.success('History cleared'); }} className="shrink-0 ml-4 px-4 py-2 rounded-md text-[13px] font-medium text-red-400 border border-red-500/30 hover:bg-red-500/10">Delete</button>
                </div>
              </div>
            </div>
          )}

          {/* ========== ACCOUNT ========== */}
          {activeTab === 'account' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-[22px] font-semibold mb-1">Account</h2>
                <p className={`text-[14px] ${subTextColor}`}>Manage your account settings.</p>
              </div>

              <div className="space-y-4">
                <div className={`p-5 rounded-xl border ${borderColor} ${cardBg} space-y-3`}>
                  <h3 className="text-[15px] font-medium">Login &amp; security</h3>
                  <div className="flex items-center justify-between">
                    <p className={`text-[13px] ${subTextColor}`}>Email</p>
                    <p className="text-[13px]">{user?.email || '—'}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className={`text-[13px] ${subTextColor}`}>Sign-in method</p>
                    <p className="text-[13px] capitalize">{user?.provider || 'email'}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className={`text-[13px] ${subTextColor}`}>Account ID</p>
                    <code className={`text-[11px] px-2 py-0.5 rounded ${isDark ? 'bg-[#08080a]' : 'bg-gray-100'}`}>{user?.id || '—'}</code>
                  </div>
                  <div className="flex items-center justify-between border-t pt-3 border-gray-700">
                    <p className={`text-[13px] ${subTextColor}`}>Change password</p>
                    <div className="flex items-center gap-2"><SoonBadge /></div>
                  </div>
                </div>

                <div className={`p-5 rounded-xl border ${borderColor} ${cardBg} flex items-center justify-between`}>
                  <div>
                    <h3 className="text-[15px] font-medium">Sign out</h3>
                    <p className={`text-[13px] ${subTextColor} mt-1`}>Sign out of Miraal on this device.</p>
                  </div>
                  <button onClick={() => { logout(); navigate('/auth'); }} className={`shrink-0 ml-4 px-4 py-2 rounded-md text-[13px] font-medium ${isDark ? 'bg-[#333] hover:bg-[#444]' : 'bg-gray-200 hover:bg-gray-300'}`}>Sign out</button>
                </div>

                {/* Honest: this only clears LOCAL browser data + signs you out.
                    Real server-side account deletion needs a backend endpoint
                    (DELETE /api/v1/user/me) that doesn't exist yet. */}
                <div className={`p-5 rounded-xl border ${borderColor} ${cardBg} flex items-center justify-between`}>
                  <div>
                    <h3 className="text-[15px] font-medium">Clear local data &amp; sign out</h3>
                    <p className={`text-[13px] ${subTextColor} mt-1`}>
                      Removes chats and settings cached in this browser and signs you out.
                      Your account and data on Miraal&rsquo;s servers are not affected.
                    </p>
                  </div>
                  <button onClick={handleClearLocalData} className={`shrink-0 ml-4 px-4 py-2 rounded-md text-[13px] font-medium ${isDark ? 'bg-[#333] hover:bg-[#444]' : 'bg-gray-200 hover:bg-gray-300'}`}>Clear &amp; sign out</button>
                </div>

                <div className="p-5 rounded-xl border border-red-500/30 bg-red-500/5">
                  <h3 className="text-[15px] font-medium text-red-500">Delete your account</h3>
                  <p className={`text-[13px] ${subTextColor} mt-1`}>
                    Permanent account deletion isn&rsquo;t self-serve yet. Email{' '}
                    <a href="mailto:support@miraal.ai" className="text-red-400 underline">support@miraal.ai</a>{' '}
                    from your registered address and we&rsquo;ll remove your account and all associated data.
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// ============================================================================
// LEGAL PAGES  (/terms, /privacy)
// Infrastructure only. The body is a clearly-marked DRAFT placeholder - the
// real legal text is drafted separately with a lawyer's review, NOT invented
// here. Routed both when signed in and signed out (a prospective user must be
// able to read these).
// ============================================================================
const LEGAL_CONTENT: Record<'terms' | 'privacy', { title: string; sections: { h: string; p: string }[] }> = {
  terms: {
    title: 'Terms of Service',
    sections: [
      { h: '1. Agreement', p: 'Placeholder. This section will describe the agreement between you and Miraal.ai for use of the service.' },
      { h: '2. Your account', p: 'Placeholder. Account eligibility, responsibility for credentials, and acceptable use.' },
      { h: '3. Plans, credits and billing', p: 'Placeholder. How paid plans, credits, renewals, and refunds work.' },
      { h: '4. Acceptable use', p: 'Placeholder. What you may and may not do with Miraal, including generated content and agent runs.' },
      { h: '5. Intellectual property', p: 'Placeholder. Ownership of your inputs, of outputs, and of the Miraal platform itself.' },
      { h: '6. Disclaimers and liability', p: 'Placeholder. AI output is not guaranteed accurate; limitation of liability.' },
      { h: '7. Changes and termination', p: 'Placeholder. How these terms may change and how either party may end the agreement.' },
      { h: '8. Contact', p: 'Questions about these terms: legal@miraal.ai' },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    sections: [
      { h: '1. What we collect', p: 'Placeholder. Account details, chats and prompts, uploaded files, usage and billing data.' },
      { h: '2. How we use it', p: 'Placeholder. To run the service, generate responses, bill you, and improve reliability.' },
      { h: '3. AI processing and sub-processors', p: 'Placeholder. Prompts and files are sent to third-party model providers to generate responses. A list of sub-processors will be maintained here.' },
      { h: '4. Retention', p: 'Placeholder. How long chats, files, and account data are kept, and what happens on deletion.' },
      { h: '5. Your rights', p: 'Placeholder. Access, export, correction, and deletion of your data (DPDP / GDPR-style). To request account deletion today, email support@miraal.ai.' },
      { h: '6. Security', p: 'Placeholder. Encryption in transit, access controls, and breach handling.' },
      { h: '7. Contact', p: 'Privacy questions or data requests: privacy@miraal.ai' },
    ],
  },
};

const LegalPage: React.FC<{ kind: 'terms' | 'privacy' }> = ({ kind }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { isAuthenticated } = useAuth();
  const doc = LEGAL_CONTENT[kind];

  return (
    <div className={`h-full min-h-screen overflow-y-auto ${isDark ? 'bg-[#08080a] text-gray-200' : 'bg-[#f9f9f9] text-gray-800'}`}>
      <div className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        <button
          onClick={() => navigate(isAuthenticated ? '/' : '/auth')}
          className={`flex items-center gap-2 text-[13px] mb-8 ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'}`}
        >
          <ArrowLeft size={15} /> Back
        </button>

        <h1 className="text-[26px] font-semibold mb-2">{doc.title}</h1>
        <p className={`text-[13px] mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Last updated: not yet published
        </p>

        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 mb-8 text-[13px] text-amber-300">
          <strong className="font-semibold">Draft — pending legal review.</strong>{' '}
          This document is a placeholder. It is not a binding {doc.title.toLowerCase()} and should not be
          relied upon. The final version will be prepared with legal counsel and published here.
        </div>

        <div className="space-y-6">
          {doc.sections.map((s) => (
            <section key={s.h}>
              <h2 className="text-[15px] font-medium mb-1.5">{s.h}</h2>
              <p className={`text-[13px] leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{s.p}</p>
            </section>
          ))}
        </div>

        <div className={`mt-10 pt-6 border-t text-[12px] ${isDark ? 'border-[#1f1f23] text-gray-500' : 'border-gray-200 text-gray-400'}`}>
          {kind === 'terms' ? (
            <>See also the <button onClick={() => navigate('/privacy')} className="underline hover:text-gray-300">Privacy Policy</button>.</>
          ) : (
            <>See also the <button onClick={() => navigate('/terms')} className="underline hover:text-gray-300">Terms of Service</button>.</>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ============================================================================
// DESIGN TOKENS (inline, kept local to this section â€” no external file needed)
// ============================================================================
// Surface:    #0e0e10 (sidebar bg)            #a3a3a3, #6b6b6f (secondary text)
// Hover:      rgba(255,255,255,0.05)          Divider: #222224 (1px hairline)
// Active:     rgba(255,255,255,0.08)          Text:    #ececec
// No gradients. No box-shadow on buttons/nav items. One consistent active-state
// treatment reused everywhere. Popover menu gets a shadow because it's a
// genuinely elevated surface â€” everything else stays flat.

const SB = {
  bg: '#111111',
  hover: 'rgba(255,255,255,0.05)',
  active: 'rgba(255,255,255,0.08)',
  border: '#2e2e2e',
  text: '#F0EFEC',
  textDim: '#B8B6B0',
  textFaint: '#8A887F',
};

// Shared item classes so nav items, menu items, and footer buttons never drift
// out of sync with each other.
const itemBase =
  'w-full flex items-center gap-2.5 rounded-md text-[13px] transition-colors duration-100 text-left';
const itemState = (active: boolean) =>
  active
    ? `text-[${SB.text}]`
    : `text-[${SB.textDim}] hover:text-[${SB.text}]`;

// ============================================================================
// USER SIDEBAR (account panel) â€” same visual language as the main sidebar,
// no separate gradient/shadow treatment.
// ============================================================================

const UserSidebar: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onOpenPricing: () => void;
  onOpenSettings: () => void;
  onLogout: () => void;
}> = ({ isOpen, onClose, onOpenPricing, onOpenSettings, onLogout }) => {
  const { t } = useLanguage();
  const profile = useUserProfile();

  if (!isOpen) return null;

  const menuItems = [
    { icon: Zap, label: t('upgradePlan'), onClick: () => { onOpenPricing(); onClose(); } },
    { icon: Settings, label: t('settings'), onClick: () => { onOpenSettings(); onClose(); } },
    { icon: Bell, label: t('notifications'), onClick: () => {} },
    { icon: HelpCircle, label: t('getHelp'), onClick: () => {} },
  ];

  return (
    <div
      className="fixed right-0 top-0 h-full w-72 flex flex-col z-[80]"
      style={{ backgroundColor: SB.bg, borderLeft: `1px solid ${SB.border}` }}
    >
      <div
        className="flex items-center justify-between px-4 py-3 shrink-0"
        style={{ borderBottom: `1px solid ${SB.border}` }}
      >
        <h3 className="font-medium text-[13px]" style={{ color: SB.text }}>{t('account')}</h3>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md transition-colors"
          style={{ color: SB.textDim }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = SB.hover; e.currentTarget.style.color = SB.text; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = SB.textDim; }}
        >
          <X size={14} />
        </button>
      </div>

      <div className="p-4">
        <div className="flex items-center gap-3 mb-5">
          <AnimatedAvatar size={40} letter={profile.name[0]?.toUpperCase() || 'U'} />
          <div className="min-w-0">
            <p className="text-[13px] font-medium truncate" style={{ color: SB.text }}>{profile.name}</p>
            <p className="text-[12px]" style={{ color: SB.textFaint }}>{profile.planName} Plan</p>
          </div>
        </div>

        <div className="space-y-0.5">
          {menuItems.map((item, i) => (
            <button
              key={i}
              onClick={item.onClick}
              className={`${itemBase} px-2.5 py-2`}
              style={{ color: SB.textDim }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = SB.hover; e.currentTarget.style.color = SB.text; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = SB.textDim; }}
            >
              <item.icon size={15} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-auto p-4 shrink-0" style={{ borderTop: `1px solid ${SB.border}` }}>
        <button
          onClick={() => { onLogout(); onClose(); }}
          className={`${itemBase} px-2.5 py-2 text-red-400`}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <LogOut size={15} />
          <span>{t('logout')}</span>
        </button>
        <div className="text-[11px] mt-3 text-center" style={{ color: SB.textFaint }}>
          Miraal.ai v{APP_VERSION}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SIDEBAR
// ============================================================================

const Sidebar: React.FC<{
  sessions: any[];
  activeSessionId: string | null;
  onSelectChat: (id: string) => void;
  onNewChat: () => void;
  onRenameChat: (id: string, title: string) => void;
  onDeleteChat: (id: string) => void;
  onTogglePin: (id: string) => void;
  onOpenPricing: () => void;
  onToggleUserSidebar: () => void;
  onLogout?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}> = ({
  sessions, activeSessionId, onSelectChat, onNewChat, onRenameChat, onDeleteChat,
  onTogglePin, onOpenPricing, onToggleUserSidebar, onLogout, collapsed = false, onToggleCollapse
}) => {
  const { t } = useLanguage();
  const profile = useUserProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const [workspaceCollapsed, setWorkspaceCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const sidebarItems = [
    { path: '/plugins', icon: Puzzle, label: t('plugins') },
    { path: '/scheduled-tasks', icon: Clock, label: t('scheduledTasks') },
    { path: '/explore', icon: CompassIcon, label: t('miraalExplore') },
    { path: '/swarm', icon: Zap, label: t('miraalSwarm') },
    { path: '/code', icon: Code2, label: t('miraalCode') },
  ];

  const aboutLinks: { icon: any; label: string; url?: string; route?: string }[] = [
    { icon: External, label: t('visitMiraal'), url: 'https://miraal.ai' },
    { icon: FileText, label: t('termsOfService'), route: '/terms' },
    { icon: Shield, label: t('privacyPolicy'), route: '/privacy' },
  ];

  // Single hover/active handler reused on every nav-style button so no two
  // interactive elements in the sidebar can drift out of sync.
  const hoverHandlers = (isActive: boolean) => ({
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      if (!isActive) e.currentTarget.style.backgroundColor = SB.hover;
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
    },
  });

  // -- COLLAPSED MODE --
  if (collapsed) {
    return (
      <div
        className="w-16 h-full flex flex-col items-center py-3"
        style={{ backgroundColor: SB.bg, borderRight: `1px solid ${SB.border}` }}
      >
        <div className="mb-4">
          <MiraalIcon size={26} />
        </div>

        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="w-9 h-9 rounded-md flex items-center justify-center transition-colors mb-2"
            style={{ color: SB.textDim }}
            {...hoverHandlers(false)}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = SB.hover; e.currentTarget.style.color = SB.text; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = SB.textDim; }}
            title="Expand sidebar"
          >
            <PanelLeftOpen size={16} className="rotate-180" />
          </button>
        )}

        <button
          onClick={onNewChat}
          className="w-9 h-9 rounded-md flex items-center justify-center transition-colors mb-3"
          style={{ backgroundColor: SB.active, color: SB.text }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = SB.active; }}
          title={t('newChat')}
        >
          <Plus size={16} />
        </button>

        <div className="flex-1 flex flex-col items-center gap-0.5 overflow-y-auto px-1 w-full">
          {sidebarItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                title={item.label}
                className="w-9 h-9 rounded-md flex items-center justify-center transition-colors"
                style={{ backgroundColor: isActive ? SB.active : 'transparent', color: isActive ? SB.text : SB.textDim }}
                {...hoverHandlers(isActive)}
              >
                <AnimatedIcon icon={item.icon} isActive={isActive} size={16} />
              </button>
            );
          })}

          <div className="mt-2 w-full">
            <ChatHistorySection
              sessions={sessions}
              activeSessionId={activeSessionId}
              onSelectChat={onSelectChat}
              onRenameChat={onRenameChat}
              onDeleteChat={onDeleteChat}
              onTogglePin={onTogglePin}
              collapsed={true}
            />
          </div>
        </div>

        <div className="mt-auto pt-3 w-full px-1" style={{ borderTop: `1px solid ${SB.border}` }}>
          <button
            onClick={onToggleUserSidebar}
            className="w-full flex items-center justify-center p-2 rounded-md transition-colors"
            {...hoverHandlers(false)}
            title={profile.name}
          >
            <AnimatedAvatar size={30} letter={profile.name[0]?.toUpperCase() || 'U'} />
          </button>
        </div>
      </div>
    );
  }

  // -- EXPANDED MODE --
  return (
    <div
      className="w-[272px] h-full flex flex-col"
      style={{ backgroundColor: SB.bg, borderRight: `1px solid ${SB.border}` }}
    >
      {/* Header */}
      <div className="px-3 py-3 flex items-center gap-2.5 shrink-0">
        <MiraalIcon size={24} />
        <div className="flex-1">
          <MiraalWordmark size={15} />
        </div>
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md transition-colors"
            style={{ color: SB.textDim }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = SB.hover; e.currentTarget.style.color = SB.text; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = SB.textDim; }}
            title="Collapse sidebar"
          >
            <PanelLeftOpen size={14} />
          </button>
        )}
      </div>

      {/* New chat */}
      <div className="px-2.5 pb-2 shrink-0">
        <button
          onClick={onNewChat}
          className={`${itemBase} px-2.5 py-2 font-medium`}
          style={{ backgroundColor: SB.active, color: SB.text }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = SB.active; }}
        >
          <Plus size={15} />
          <span>{t('newChat')}</span>
        </button>
      </div>

      {/* Workspace nav */}
      <div className="px-2.5 py-1 shrink-0">
        <button
          onClick={() => setWorkspaceCollapsed(!workspaceCollapsed)}
          className="w-full flex items-center justify-between px-2 py-1.5 text-[12px] transition-colors"
          style={{ color: SB.textFaint }}
        >
          <span>{t('miraalWorkspace')}</span>
          <ChevronDown size={13} className={`transition-transform ${workspaceCollapsed ? '-rotate-90' : ''}`} />
        </button>
        {!workspaceCollapsed && (
          <div className="mt-0.5 space-y-0.5">
            {sidebarItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`${itemBase} px-2 py-1.5`}
                  style={{ backgroundColor: isActive ? SB.active : 'transparent', color: isActive ? SB.text : SB.textDim }}
                  {...hoverHandlers(isActive)}
                >
                  <AnimatedIcon icon={item.icon} isActive={isActive} size={14} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Chat history */}
      <div className="flex-1 min-h-0 overflow-hidden mt-1" style={{ borderTop: `1px solid ${SB.border}` }}>
        <ChatHistorySection
          sessions={sessions}
          activeSessionId={activeSessionId}
          onSelectChat={onSelectChat}
          onRenameChat={onRenameChat}
          onDeleteChat={onDeleteChat}
          onTogglePin={onTogglePin}
        />
      </div>

      {/* Account footer */}
      <div className="shrink-0 p-2.5" style={{ borderTop: `1px solid ${SB.border}` }}>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md transition-colors"
            {...hoverHandlers(false)}
          >
            <AnimatedAvatar size={28} letter={profile.name[0]?.toUpperCase() || 'U'} />
            <div className="flex-1 text-left min-w-0">
              <div className="text-[13px] font-medium truncate" style={{ color: SB.text }}>{profile.name}</div>
              <div className="text-[12px] truncate" style={{ color: SB.textFaint }}>{profile.planName} Plan</div>
            </div>
            <ChevronUp
              size={13}
              className={`shrink-0 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
              style={{ color: SB.textDim }}
            />
          </button>

          {menuOpen && (
            <div
              className="absolute bottom-full left-0 right-0 mb-2 rounded-lg py-1 z-50"
              style={{ backgroundColor: '#18181b', border: `1px solid ${SB.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}
            >
              <button
                onClick={() => { onOpenPricing(); setMenuOpen(false); }}
                className={`${itemBase} px-3 py-2`}
                style={{ color: SB.textDim }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = SB.hover; e.currentTarget.style.color = SB.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = SB.textDim; }}
              >
                <Zap size={14} />
                <span>{t('upgradePlan')}</span>
              </button>

              {/* Was missing here entirely - the expanded sidebar's account
                  menu had Upgrade Plan / About / Logout but no way to reach
                  Settings at all. The collapsed-sidebar avatar opens
                  UserSidebar (which does have it), so this was reachable only
                  by first collapsing the sidebar - not obvious. */}
              <button
                onClick={() => { navigate('/settings'); setMenuOpen(false); }}
                className={`${itemBase} px-3 py-2`}
                style={{ color: SB.textDim }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = SB.hover; e.currentTarget.style.color = SB.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = SB.textDim; }}
              >
                <Settings size={14} />
                <span>{t('settings')}</span>
              </button>

              <button
                onClick={() => setAboutOpen(!aboutOpen)}
                className={`${itemBase} px-3 py-2`}
                style={{ color: SB.textDim }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = SB.hover; e.currentTarget.style.color = SB.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = SB.textDim; }}
              >
                <Info size={14} />
                <span>{t('aboutMiraal')}</span>
                <ChevronRight size={13} className={`ml-auto transition-transform ${aboutOpen ? 'rotate-90' : ''}`} />
              </button>

              {aboutOpen && (
                <div className="px-1 pb-1">
                  <div className="rounded-md overflow-hidden" style={{ backgroundColor: '#101012' }}>
                    {aboutLinks.map((item, i) =>
                      item.route ? (
                        <button
                          key={i}
                          onClick={() => { navigate(item.route!); setAboutOpen(false); setMenuOpen(false); }}
                          className="w-full flex items-center gap-2.5 px-2.5 py-2 text-[12px] transition-colors text-left"
                          style={{ color: SB.textDim }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = SB.text; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = SB.textDim; }}
                        >
                          <item.icon size={13} />
                          {item.label}
                        </button>
                      ) : (
                        <a
                          key={i}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2.5 px-2.5 py-2 text-[12px] transition-colors"
                          style={{ color: SB.textDim }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = SB.text; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = SB.textDim; }}
                        >
                          <item.icon size={13} />
                          {item.label}
                        </a>
                      )
                    )}
                  </div>
                  {/* Open-source attribution. CC-BY-4.0 voices require credit. */}
                  <div
                    className="px-2.5 py-2 mt-1 rounded-md text-[10px] leading-relaxed"
                    style={{ backgroundColor: '#101012', color: SB.textDim }}
                  >
                    <div className="uppercase tracking-wider text-[9px] mb-1" style={{ color: SB.textDim }}>
                      Voice credits
                    </div>
                    English narration “Dhun” (voice model: kristin) — LibriVox, public domain.{' '}
                    Telugu “padmavathi” — AI4Bharat IndicVoices-R, CC&nbsp;BY&nbsp;4.0.{' '}
                    Hindi — SYSPIN (IISc SPIRE Lab), CC&nbsp;BY&nbsp;4.0.{' '}
                    Synthesis: Piper &amp; Coqui&nbsp;VITS. Transcription: faster-whisper.
                  </div>
                </div>
              )}

              <div style={{ borderTop: `1px solid ${SB.border}`, marginTop: '4px' }}>
                <button
                  onClick={() => { onLogout?.(); setMenuOpen(false); }}
                  className={`${itemBase} px-3 py-2 text-red-400 mt-1`}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(248,113,113,0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <LogOut size={14} />
                  <span>{t('logout')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// APP CONTENT
// ============================================================================

const AppContent: React.FC = () => {
  const { isAuthenticated, logout, isLoading: isAuthLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pricingOpen, setPricingOpen] = useState(false);
  const [userSidebarOpen, setUserSidebarOpen] = useState(false);

  // Let deep-nested screens (Settings -> Subscription) open the pricing modal
  // without prop-drilling through the router.
  useEffect(() => {
    const open = () => setPricingOpen(true);
    window.addEventListener('miraal-open-pricing', open);
    return () => window.removeEventListener('miraal-open-pricing', open);
  }, []);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => ls.get('miraal_sidebar_collapsed', false));

  const {
    sessions,
    activeSessionId,
    createNewChat,
    selectChat,
    renameChat,
    deleteChat,
    togglePin,
  } = useChatStoreContext();

  const handleNewChat = () => { createNewChat(); navigate('/'); };

  const handleSelectChat = (id: string) => {
    selectChat(id);
    if (location.pathname !== '/') { navigate('/'); }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const toggleSidebarCollapse = useCallback(() => {
    setSidebarCollapsed((prev: boolean) => {
      const next = !prev;
      ls.set('miraal_sidebar_collapsed', next);
      return next;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'm') {
        e.preventDefault();
        navigate('/settings');
      }
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        toggleSidebarCollapse();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, toggleSidebarCollapse]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    const checkAlerts = async () => {
      try {
        const { alerts } = await getSpendAlerts();
        if (cancelled) return;
        alerts.forEach((alert) => {
          const isFull = alert.thresholdPct >= 100;
          toast(
            (t) => (
              <div className="flex flex-col gap-2">
                <span className="text-sm">
                  {isFull
                    ? `You've reached 100% of your ${alert.plan} plan's monthly spend limit (\u20b9${alert.limitAtTrigger}).`
                    : `You've used ${alert.thresholdPct}% of your ${alert.plan} plan's monthly spend limit (\u20b9${alert.spendAtTrigger.toFixed(2)} of \u20b9${alert.limitAtTrigger}).`}
                </span>
                <button
                  onClick={async () => {
                    await acknowledgeSpendAlert(alert.id);
                    toast.dismiss(t.id);
                  }}
                  className="self-end text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                >
                  Dismiss
                </button>
              </div>
            ),
            { duration: isFull ? Infinity : 10000, id: `spend-alert-${alert.id}` }
          );
        });
      } catch {
        // silent - alert polling failure should not disrupt the app
      }
    };

    checkAlerts();
    const interval = setInterval(checkAlerts, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  // Placed after every hook above (Rules of Hooks - isAuthLoading flips
  // true->false within the same mounted instance, so a hook called only in
  // one of the two states would break). Without this, the very first
  // render - before restoreSession()'s /auth/me check resolves - falls
  // through to isAuthenticated===false below and briefly flashes the
  // signed-out auth page/background before flipping to the real app, a
  // real visible flash-of-wrong-content confirmed live on every hard
  // reload. This branded loading screen covers that gap instead.
  if (isAuthLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[color:var(--bg,#151515)]">
        <MiraalMonogram size={40} spinning />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<WebsiteV2 />} />
        <Route path="/pricing" element={<WebsiteV2 page="pricing" />} />
        <Route path="/products/chat" element={<WebsiteV2 page="chat" />} />
        <Route path="/products/code" element={<WebsiteV2 page="code" />} />
        <Route path="/products/swarm" element={<WebsiteV2 page="swarm" />} />
        <Route path="/products/explore" element={<WebsiteV2 page="explore" />} />
        <Route path="/products/marketplace" element={<WebsiteV2 page="marketplace" />} />
        <Route path="/products/media" element={<WebsiteV2 page="media" />} />
        <Route path="/products/create" element={<WebsiteV2 page="media" />} />
        <Route path="/agents" element={<WebsiteV2 page="marketplace" />} />
        <Route path="/about" element={<WebsiteV2 page="about" />} />
        <Route path="/faq" element={<WebsiteV2 page="faq" />} />
        <Route path="/contact" element={<WebsiteV2 page="contact" />} />
        <Route path="/security" element={<WebsiteV2 page="security" />} />
        {websiteApplicationRoutes.filter(path => path !== '/').map(path => (
          <Route key={path} path={path} element={<Navigate to={`/auth?next=${encodeURIComponent(path)}`} replace />} />
        ))}
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/login" element={<Navigate to="/auth" replace />} />
        <Route path="/register" element={<Navigate to="/auth" replace />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Public - a prospective user must be able to read these */}
        <Route path="/terms" element={<LegalPage kind="terms" />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const isChromeless = ['/settings', '/terms', '/privacy'].includes(location.pathname);

  return (
    <div className="h-screen w-screen bg-[color:var(--bg,#151515)] flex overflow-hidden">
      <Toaster position="top-right" toastOptions={{ style: { background: '#111', color: '#fff', border: '1px solid #333' } }} />

      {!isChromeless && (
        <Sidebar 
          sessions={sessions.filter((s: any) => !s.title?.startsWith('Scheduled: '))} 
          activeSessionId={activeSessionId} 
          onSelectChat={handleSelectChat} 
          onNewChat={handleNewChat} 
          onRenameChat={renameChat} 
          onDeleteChat={deleteChat} 
          onTogglePin={togglePin} 
          onOpenPricing={() => setPricingOpen(true)}
          onToggleUserSidebar={() => setUserSidebarOpen(true)}
          onLogout={handleLogout}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapse}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0 relative pb-[env(safe-area-inset-bottom)] md:pb-0">
        <React.Suspense fallback={<div role="status" className="p-8 text-gray-300">Loading workspace…</div>}>
        <Routes>
          <Route path="/" element={<MiraalChatContainer onNewChat={handleNewChat} />} />
          <Route path="/plugins" element={<PluginsPage />} />
          <Route path="/scheduled-tasks" element={<ScheduledTasksPage />} />
          <Route path="/explore" element={<MiraalExplorePage />} />
          <Route path="/settings" element={<SettingsPageFixed />} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/teams" element={<TeamsPage />} />
          <Route path="/auth" element={<Navigate to={websiteSignInDestination(location.search)} replace />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/register" element={<Navigate to="/" replace />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/swarm" element={<AgentSwarmPage />} />
          <Route path="/code" element={<CodePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </React.Suspense>
      </div>

      <UserSidebar 
        isOpen={userSidebarOpen} 
        onClose={() => setUserSidebarOpen(false)} 
        onOpenPricing={() => setPricingOpen(true)} 
        onOpenSettings={() => navigate('/settings')}
        onLogout={handleLogout}
      />
      <PricingOverlay isOpen={pricingOpen} onClose={() => setPricingOpen(false)} />
      <MobileNav />
    </div>
  );
};

// ============================================================================
// APP ROOT
// ============================================================================

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <LanguageProvider>
          <ChatStoreProvider>
            <SwarmProvider>
              <OrchestratorProvider>
                <AppContent />
              </OrchestratorProvider>
            </SwarmProvider>
          </ChatStoreProvider>
        </LanguageProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
